// Third party library imports
import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import Keycloak from 'keycloak-js';

// Zopup library imports
import { CompanyStore, TenantStore, UserStore, UOMRepositoryStore, UomPreferenceStore } from '@zopup/zop-apis';
import { SnackBarService, StorageKey, StorageService } from '@zopup/zop-shared';
import {
    CompanyDto, SubscriptionStatus,
    TenantCompactDto,
    TenantDto,
    UserDto,
    UserUpdateCommand
} from '@zopup/zop-types';
import { zopAnimations, ZopBrandingService, ZopConfirmationService } from '@zopup/zop-ux-core';

// Internal project imports
import { TokenParserService } from '../../services/token-parser/token-parser.service';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { ZopAuthConfigService } from '../../providers/zop-auth-config.provider';

@Component({
    selector: 'zop-app-tenant-selection',
    templateUrl: './tenant-selection.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: zopAnimations,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        RouterModule,
        UserAvatarComponent,
    ],
})
export class TenantSelectionComponent implements OnInit {
    readonly tenantStore = inject(TenantStore);
    readonly companyStore = inject(CompanyStore);
    readonly userStore = inject(UserStore);
    readonly branding = inject(ZopBrandingService);
    readonly uomStore = inject(UOMRepositoryStore);
    readonly uomPrefStore = inject(UomPreferenceStore);

    private readonly keycloak = inject(Keycloak);
    private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
    private readonly tokenParserService = inject(TokenParserService);
    private readonly authConfig = inject(ZopAuthConfigService);

    private authenticationProcessed = false; // Flag to prevent multiple calls

    tenants: TenantWrapperDto[];
    user: UserDto;
    configInviationForm: UntypedFormGroup;
    selectedTenant: TenantWrapperDto;
    
    constructor(
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _snackBarService: SnackBarService,
        private _storageService: StorageService,
        private _confirmationService: ZopConfirmationService,
    ) {
        effect(() => {
            const keycloakEvent = this.keycloakSignal();
            
            switch (keycloakEvent?.type) {
                case KeycloakEventType.Ready:
                    const authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);
                    if (authenticated) {
                        this.handleSuccessfulAuthentication();
                    } else {
                         this.signOut();
                    }
                    break;

                case KeycloakEventType.AuthSuccess:
                    this.handleSuccessfulAuthentication();
                    break;

                case KeycloakEventType.AuthLogout:
                     this.signOut();
                    break;
            }
        });
    }

    ngOnInit(): void {
        // Handle page refresh scenarios where Keycloak is already authenticated
        // but the effect hasn't triggered yet or data needs to be loaded
        this.handleSuccessfulAuthentication();
    }

    getTenants(): void {
        try {
            this.tenants = [];

            const tenants = this._storageService.getItem<TenantCompactDto[]>(
                StorageKey.tenants
            );

            tenants?.forEach((t) => {
                const tenantWrapper = new TenantWrapperDto();
                tenantWrapper.tenant = t;
                tenantWrapper.type = '';
                this.tenants.push(tenantWrapper);
            });
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => getTenants()'
            );
            console.error(ex);
        }
    }

    async createTenant(): Promise<void> {
        try {
            this._router.navigateByUrl(this.authConfig.getConfig().routes.tenantCreate);
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => createTenant()'
            );
            console.error(ex);
        }
    }

    async onTenantSelect(tenant: TenantWrapperDto): Promise<void> {
        try {
            if (tenant.tenant.invitationStatus !== 'Accepted') {
                this.openInvitationConfirmationDialog(tenant);
            } else {
                await this.tenantStore.getById(tenant.tenant.id!);

                // Check if the tenant has active subscription - START
                const subscriptionFound = await this.checkSubscriptionStatus(
                    tenant
                );

                this._storageService.setItem<TenantDto>(
                    StorageKey.tenant,
                    this.tenantStore.selectedTenant()
                );
                this.selectedTenant = tenant;

                // Initialize UOM system after tenant selection
                await this.initializeUomSystem();

                if (!subscriptionFound) {
                    // Check if there's a specific subscription error
                    if (tenant.type === 'error' && tenant.subscriptionError) {
                        this._snackBarService.showError(tenant.subscriptionError);
                        return;
                    }
                    
                    // Default handling for no subscription
                    if (
                        this._storageService.user.email.value ===
                        this.tenantStore.selectedTenant().email?.value
                    ) {
                        this._router.navigateByUrl(this.authConfig.getConfig().routes.packageSelection);
                    } else {
                        this._snackBarService.showError('You do not have an active subscription for this tenant. Please contact your administrator.');
                    }
                    return;
                }
                // Check if the tenant has active subscription - END

                // Check if the company exists - START
                const companyFound = await this.checkCompanyStatus(tenant);

                if (!companyFound) {
                    this._router.navigateByUrl(this.authConfig.getConfig().routes.companyCreate);
                    return;
                }

                this._storageService.setItem<CompanyDto>(
                    StorageKey.company,
                    this.companyStore.selectedCompany()
                );
                // Check if the company exists - END

                // Route to package selection screen
                this._router.navigateByUrl(this.authConfig.getConfig().routes.branchSelection);
            }
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => onTenantSelect()'
            );
            console.error(ex);
            this._snackBarService.showError(ex);
        }
    }

    async updateUser(command: UserUpdateCommand): Promise<void> {
        try {
            this.tenants = [];
            const userResponse = await this.userStore.update(command);
            if (userResponse.isSuccess) {
                this._storageService.user = userResponse.result!;
                userResponse.result?.tenants?.forEach((t) => {
                    const tenantWrapper = new TenantWrapperDto();
                    tenantWrapper.tenant = t;
                    tenantWrapper.type = '';
                    this.tenants.push(tenantWrapper);
                });
                this.tenants = this.tenants.filter(
                    (t) =>
                        t.tenant.roles &&
                        t.tenant.roles.length > 0 &&
                        !(
                            t.tenant.roles.length === 1 &&
                            t.tenant.roles[0] === 'User'
                        )
                );
            } else {
                // TODO
            }
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => updateUser()'
            );
            console.error(ex);
        }
    }

    async checkSubscriptionStatus(tenant: TenantWrapperDto): Promise<boolean> {
        try {
            if (this.tenantStore.selectedTenant()) {
                const subscriptions = this.tenantStore.selectedTenant().subscriptions;
                
                if (!subscriptions || subscriptions.length === 0) {
                    return false;
                }

                // Find the most recent subscription based on CreatedAt (equivalent to OrderByDescending(x => x.CreatedAt).FirstOrDefault())
                const currentSubscription = [...subscriptions]
                    .sort((a, b) => {
                        if (!a.createdAt && !b.createdAt) return 0;
                        if (!a.createdAt) return 1;
                        if (!b.createdAt) return -1;
                        return new Date(b.createdAt.toString()).getTime() - new Date(a.createdAt.toString()).getTime();
                    })[0];

                if (!currentSubscription) {
                    return false;
                }

                // Check for active subscriptions - Allow user to continue
                if (currentSubscription.status === SubscriptionStatus.Active ||
                    currentSubscription.status === SubscriptionStatus.TrialStarted ||
                    currentSubscription.status === SubscriptionStatus.TrailExtensionStarted) {
                    
                    // User has valid active subscription - allow tenant selection to continue
                    return true;
                }

                // Check for future subscriptions - Block with error message
                if (currentSubscription.status === SubscriptionStatus.Future ||
                    currentSubscription.status === SubscriptionStatus.TrialFuture) {
                    
                    const errorMessage = currentSubscription.status === SubscriptionStatus.Future 
                        ? 'Future subscription already exists. Please cancel it before creating a new one.'
                        : 'Future (Trial) subscription already exists. Please cancel it before creating a new one.';
                    
                    tenant.type = 'error';
                    tenant.subscriptionError = errorMessage;
                    return false; // Block execution
                }

                // Check for paused subscription - Block with error message
                if (currentSubscription.status === SubscriptionStatus.Paused) {
                    tenant.type = 'error';
                    tenant.subscriptionError = 'Paused subscription found. Please cancel it before creating a new one';
                    return false; // Block execution
                }

                // Check for subscription scheduled for cancellation - Block with error message
                if (currentSubscription.status === SubscriptionStatus.ScheduledForCancellation) {
                    tenant.type = 'error';
                    tenant.subscriptionError = 'Active subscription scheduled for cancellation. Please use change package mechanism.';
                    return false; // Block execution
                }

                // For any other status (expired, cancelled, etc.) - No active subscription found
                return false;
            }
            return false;
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => checkSubscriptionStatus()'
            );
            console.error(ex);
            return false;
        }
    }

    async checkCompanyStatus(tenant: TenantWrapperDto): Promise<boolean> {
        try {
            await this.companyStore.getById(tenant.tenant.id!);
            if (this.companyStore.selectedCompany()) {
                return true;
            }
            return false;
        } catch (ex) {
            console.error(
                'Exception in tenant-selection.component => checkCompanyStatus()'
            );
            console.error(ex);
            return false;
        }
    }

    openInvitationConfirmationDialog(tenant: TenantWrapperDto): void {
        this.createInvitationForm();
        // Open the dialog and save the reference of it
        const dialogRef = this._confirmationService.open(
            this.configInviationForm.value
        );

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            // Check if the 'close' icon was not clicked
            if (result !== undefined) {
                const user: UserDto = { ...this.user };
                user.tenants.find(
                    (t) => t.id === tenant.tenant.id
                ).invitationStatus =
                    result === 'confirmed' ? 'Accepted' : 'Rejected';
                const userCommand = new UserUpdateCommand();
                userCommand.userDetails = user;
                this.updateUser(userCommand);
            }
        });
    }

    private createInvitationForm() {
        this.configInviationForm = this._formBuilder.group({
            title:
                'You have been invited to join ' +
                this.selectedTenant?.tenant?.name +
                '!',
            message:
                'The invitation has not been accepted yet. Would you like to accept?',
            icon: this._formBuilder.group({
                show: false,
                name: 'heroicons_outline:check',
                color: 'primary',
            }),
            actions: this._formBuilder.group({
                confirm: this._formBuilder.group({
                    show: true,
                    label: 'Accept',
                    color: 'primary',
                }),
                cancel: this._formBuilder.group({
                    show: true,
                    label: 'Decline',
                    color: 'warn',
                }),
            }),
            dismissible: true,
        });
    }

    getUserName(): string {
        if (this.user) {
            if (this.user.displayName) {
                return this.user.displayName;
            } else {
                return this.user.email.value;
            }
        }
        return '';
    }

    async signOut(): Promise<void> {
        // Clear local state before redirecting away; with `login-required` we may never see AuthLogout events.
        this._storageService.clearStorage();
        await this.keycloak.logout({
            redirectUri: window.location.origin + '/sign-in'
        });
    }

    /**
     * Initialize UOM system after tenant selection
     * - Loads all active UOMs into cache
     * - Loads tenant preferences and region profiles
     */
    private async initializeUomSystem(): Promise<void> {
        try {
            console.log('[UOM System] Initializing after tenant selection...');

            // Load all active UOMs (small dataset ~100-200 records)
            await this.uomStore.fetchPage({ isActive: true });
            console.log('[UOM System] UOMs loaded:', Object.keys(this.uomStore.entities()).length);

            // Load tenant preferences
            await this.uomPrefStore.initialize();
            console.log('[UOM System] Preferences loaded for tenant:', this.selectedTenant.tenant.id);

            console.log('[UOM System] Initialization complete');
        } catch (error) {
            console.error('[UOM System] Failed to initialize:', error);
            // Don't block tenant selection on UOM initialization failure
        }
    }

    private async handleSuccessfulAuthentication(): Promise<void> {
        // Prevent multiple calls
        if (this.authenticationProcessed) {
            // Authentication already processed, skipping...
            return;
        }

        if (this.keycloak.authenticated) {
            this.authenticationProcessed = true;

            try {
                // Use the token parser service to parse and store token information
                await this.tokenParserService.parseToken(this.keycloak);
                this.user = this._storageService.user;
                
                // Get tenants after parsing is complete
                this.getTenants();
            } catch (error) {
                console.error('❌ TenantSelection: Error during authentication processing:', error);
                this.authenticationProcessed = false; // Reset on error
            }
        } else {
            // User is not authenticated, redirect to sign-in
            console.log('🔐 TenantSelection: User not authenticated, redirecting to sign-in...');
            this.signOut();
        }
    }
}

class TenantWrapperDto {
    tenant!: TenantCompactDto;
    type!: 'error' | '';
    subscriptionError!: string;
}
